/**
 * iap.js — Apple In-App Purchase via RevenueCat. NATIVE (iOS) ONLY.
 *
 * Web coaches keep subscribing on the website (Stripe). This powers the in-app
 * subscribe option Apple requires for the iOS build. Every function is a no-op
 * on web. The RevenueCat "platform" entitlement is the source of truth for IAP
 * access; a webhook also mirrors it into Supabase for server-side records.
 *
 * The RevenueCat plugin is lazy-imported so it never loads in the web bundle.
 */
import { isNative } from "./platform";

// Public RevenueCat SDK key for Apple — safe to ship in the client (it's a
// publishable key). Set REACT_APP_RC_APPLE_KEY at build time, or paste the
// appl_… key into the fallback below.
const RC_APPLE_KEY = process.env.REACT_APP_RC_APPLE_KEY || "appl_umcAKUkSdNLEFJPBTiSmQDvxvmN";
const PRODUCT_ID = "com.byanymeansbball.coaches.monthly";

let _Purchases = null;
let _configured = false;
let _configuring = null;   // the one configure() in flight, shared by every caller
let _userId = null;

async function rc() {
  if (_Purchases) return _Purchases;
  const mod = await import("@revenuecat/purchases-capacitor");
  _Purchases = mod.Purchases;
  return _Purchases;
}

// This app sells one subscription, so ANY active entitlement = subscribed. We
// don't hardcode the entitlement id (RevenueCat auto-names it), which keeps
// access working regardless of what the entitlement is called in the dashboard.
const entActive = (customerInfo) => Object.keys(customerInfo?.entitlements?.active || {}).length > 0;

/**
 * Configure RevenueCat once and tie purchases to the Supabase user id so the
 * webhook can map an IAP back to the right account. Idempotent — safe to call
 * on every auth change. Call after sign-in.
 *
 * Boot calls this from two places at once. It used to set _configured only
 * after configure() resolved, so the second caller configured again, and a
 * check that ran in between read "not configured" as "not subscribed": an
 * iOS subscriber saw the paywall. Now every caller waits on the same promise.
 */
export async function initIAP(supabaseUserId) {
  if (!isNative()) return;
  try {
    const P = await rc();
    if (!_configured) {
      if (!_configuring) {
        _userId = supabaseUserId || null;
        _configuring = P.configure({ apiKey: RC_APPLE_KEY, appUserID: supabaseUserId || undefined })
          .then(() => { _configured = true; })
          .catch((e) => { _configuring = null; throw e; });
      }
      await _configuring;
    }
    if (supabaseUserId && supabaseUserId !== _userId) {
      try { await P.logIn({ appUserID: supabaseUserId }); _userId = supabaseUserId; } catch { /* non-fatal */ }
    }
  } catch (e) { console.warn("[iap] init:", e?.message || e); }
}

async function ready() {
  if (_configured) return true;
  if (_configuring) { try { await _configuring; } catch { /* fall through */ } }
  return _configured;
}

/** Is the Apple entitlement active? true / false, or null when RevenueCat
 *  couldn't say (not configured, offline, an error): callers must not read
 *  null as "not subscribed". One retry, since a cold start often fails once. */
export async function isIAPActive() {
  if (!isNative()) return false;
  if (!(await ready())) return null;
  const P = await rc();
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { customerInfo } = await P.getCustomerInfo();
      return entActive(customerInfo);
    } catch {
      if (attempt === 0) await new Promise(r => setTimeout(r, 1200));
    }
  }
  return null;
}

/** The monthly StoreKit product + its localized price for the subscribe screen.
 *  Fetched directly by product id (no RevenueCat offering/package needed). */
export async function getMonthlyOffer() {
  if (!isNative() || !(await ready())) return null;
  try {
    const P = await rc();
    // type:"subs" is important — without it the SDK can default to fetching
    // non-subscription products and silently return nothing for our sub.
    const { products } = await P.getProducts({ productIdentifiers: [PRODUCT_ID], type: "subs" });
    const product = products?.[0];
    if (!product) { console.warn("[iap] product not found in store:", PRODUCT_ID); return null; }
    return {
      product,
      priceString: product.priceString || "",
      title: product.title || "BAM Coaches Platform",
    };
  } catch (e) { console.warn("[iap] getProducts:", e?.message || e); return null; }
}

/** Run the App Store purchase flow. Returns true if the entitlement is active. */
export async function purchaseMonthly(product) {
  if (!isNative() || !product) return false;
  const P = await rc();
  try {
    const res = await P.purchaseStoreProduct({ product });
    return entActive(res?.customerInfo);
  } catch (e) {
    // User-cancelled is a normal, silent outcome — not an error to surface.
    if (e?.userCancelled || e?.code === "1" || /cancel/i.test(e?.message || "")) return false;
    throw e;
  }
}

/** Restore a prior purchase (new device / reinstall). Returns true if active. */
export async function restoreIAP() {
  if (!isNative()) return false;
  try {
    const P = await rc();
    const { customerInfo } = await P.restorePurchases();
    return entActive(customerInfo);
  } catch (e) { console.warn("[iap] restore:", e?.message || e); return false; }
}
